import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createParagraphNode,
  $getSelection,
  $isDecoratorNode,
  $isNodeSelection,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_ENTER_COMMAND,
} from "lexical";
import { useEffect } from "react";

/**
 * Handles Enter and Backspace/Delete when a single block-level DecoratorNode
 * (image, video embed, etc.) is selected via NodeSelection.
 *
 * - Enter → inserts a paragraph after the node and moves the cursor there.
 * - Backspace / Delete → removes the node.
 */
export function DecoratorBlockPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    function getSelectedDecoratorNode() {
      const selection = $getSelection();
      if (!$isNodeSelection(selection)) return null;
      const nodes = selection.getNodes();
      if (nodes.length === 1 && $isDecoratorNode(nodes[0])) return nodes[0];
      return null;
    }

    const unregisterEnter = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        const node = getSelectedDecoratorNode();
        if (!node) return false;
        event?.preventDefault();
        const paragraph = $createParagraphNode();
        node.insertAfter(paragraph);
        paragraph.selectEnd();
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );

    const unregisterBackspace = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event) => {
        const node = getSelectedDecoratorNode();
        if (!node) return false;
        event?.preventDefault();
        node.remove();
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );

    const unregisterDelete = editor.registerCommand(
      KEY_DELETE_COMMAND,
      (event) => {
        const node = getSelectedDecoratorNode();
        if (!node) return false;
        event?.preventDefault();
        node.remove();
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );

    return () => {
      unregisterEnter();
      unregisterBackspace();
      unregisterDelete();
    };
  }, [editor]);

  return null;
}
